import { useState, useMemo, Fragment, useCallback, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TablePagination,
    IconButton,
    TableSortLabel,
  } from '@mui/material';
  import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
  import { useDateSort } from '../../../../../hooks/useDateSort';
  import TableActionsMenu from '../../../../../components/common/TableActionsMenu';
  import { 
    AdcashAvatyeDetailMetricsByPlacement,
    AdcashAvatyeDetailMetricsByPlacementDailyStats
  } from '../../../../../types/adcash.types';
  
  type Props = {
    data: AdcashAvatyeDetailMetricsByPlacement;
    exchangeRate: number;
    onExchangeRateChange: (rate: number) => void;
  };

  type DateSummary = {
    date: string;
    totalRevenue: number;
    totalReq: number;
    totalRes: number;
    totalImp: number;
    totalClk: number;
    placements: AdcashAvatyeDetailMetricsByPlacementDailyStats[];
  };

  export default function AdcashAvatyePlacementTable({ data, exchangeRate, onExchangeRateChange }: Props) {
    const { dailyStats } = data;
    const rowsPerPage = 31;
    const [openDateRows, setOpenDateRows] = useState<Record<string, boolean>>({});
    const [page, setPage] = useState(0);
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    // 날짜별로 그룹화하고 총합 계산
    const dateSummariesRaw = useMemo(() => {
      const grouped = dailyStats.reduce((acc, item) => {
        if (!acc[item.date]) {
          acc[item.date] = {
            date: item.date,
            totalRevenue: 0,
            totalReq: 0,
            totalRes: 0,
            totalImp: 0,
            totalClk: 0,
            placements: []
          };
        }
        acc[item.date].totalRevenue += item.revenue;
        acc[item.date].totalReq += item.req;
        acc[item.date].totalRes += item.res;
        acc[item.date].totalImp += item.imp;
        acc[item.date].totalClk += item.clk;
        acc[item.date].placements.push(item);
        return acc;
      }, {} as Record<string, DateSummary>);

      return Object.values(grouped);
    }, [dailyStats]);

    const { sorted: dateSummaries, sortDirection, toggleSort } = useDateSort(dateSummariesRaw);

    // 페이지네이션된 데이터
    const paginatedSummaries = useMemo(() => {
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      return dateSummaries.slice(startIndex, endIndex);
    }, [dateSummaries, page, rowsPerPage]);

    const toggleDateRow = (date: string) => {
      setOpenDateRows(prev => ({ ...prev, [date]: !prev[date] }));
    };

    const formatCurrency = useCallback((value: number) => {
      return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    }, []);

    const formatNumberOnly = useCallback((value: number) => Math.round(value).toLocaleString('ko-KR'), []);

    const calculateFillRate = (req: number, res: number) => {
      return req > 0 ? ((res / req) * 100).toFixed(2) : '0.00';
    };

    const calculateCTR = (imp: number, clk: number) => {
      return imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
    };

    const calculateECPM = useCallback((revenue: number, imp: number) => {
      return imp > 0 ? formatNumberOnly((revenue / imp) * 1000) : '0';
    }, [formatNumberOnly]);

    // 엑셀 다운로드용 전체 데이터 (날짜별 요약만, 현재 정렬 상태 그대로)
    // 퍼센트 컬럼은 소수로 전달
    const allRowsDataForExcel = useMemo(() => {
      return dateSummaries.map((summary) => {
        // fillRate와 CTR을 소수 형태로 (0.xx)
        const totalFillRate = summary.totalReq > 0 ? summary.totalRes / summary.totalReq : 0;
        const totalCTR = summary.totalImp > 0 ? summary.totalClk / summary.totalImp : 0;
        const totalECPM = calculateECPM(summary.totalRevenue * exchangeRate, summary.totalImp);
        return [
          summary.date,
          formatNumberOnly(summary.totalRevenue * exchangeRate),
          summary.totalReq.toLocaleString(),
          summary.totalRes.toLocaleString(),
          totalFillRate,
          summary.totalImp.toLocaleString(),
          summary.totalClk.toLocaleString(),
          totalCTR,
          totalECPM
        ];
      });
    }, [dateSummaries, exchangeRate, formatNumberOnly, calculateECPM]);

    return (
      <Box>
        {/* 환율 버튼 1230 or 1350 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mt: 2
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[1230, 1350].map(rate => (
              <Button
                key={rate}
                size="small"
                variant="outlined"
                onClick={() => onExchangeRateChange(rate)}
                sx={{
                  borderColor: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                  color: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: exchangeRate === rate ? '#0044D2' : '#757575',
                    backgroundColor: exchangeRate === rate ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                  }
                }}
              >
                환율 {rate}
              </Button>
            ))}
          </Box>
          <TableActionsMenu tableRef={tableRef} allRowsData={allRowsDataForExcel} />
        </Box>
        <TableContainer
          sx={{
            overflowX: 'auto',
            mt: 2
          }}
        >
          <Table
            ref={tableRef}
            size="small"
            sx={{
              borderCollapse: 'collapse',
              width: '100%'
            }}
          >
            <TableHead>
              <TableRow sx={{ height: 36 }}>
                <TableCell sx={{ width: 40, px: '4px', fontWeight: 'bold' }}></TableCell>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>
                  <TableSortLabel
                    active
                    direction={sortDirection}
                    onClick={toggleSort}
                  >
                    날짜
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>총 매출</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>request</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>response</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>fill rate</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>impression</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>click</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>ctr</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>eCPM</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedSummaries.map(summary => {
                const dateKey = summary.date;
                const isOpen = openDateRows[dateKey];
                const totalFillRate = calculateFillRate(summary.totalReq, summary.totalRes);
                const totalCTR = calculateCTR(summary.totalImp, summary.totalClk);
                const totalECPM = calculateECPM(summary.totalRevenue * exchangeRate, summary.totalImp);

                return (
                  <Fragment key={dateKey}>
                    {/* 날짜 요약 행 */}
                    <TableRow 
                      sx={{ 
                        backgroundColor: '#fff', 
                        height: 32,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      <TableCell sx={{ px: '4px' }}>
                        <IconButton size="small" onClick={() => toggleDateRow(dateKey)}>
                          {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>{summary.date}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {formatCurrency(summary.totalRevenue * exchangeRate)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {summary.totalReq.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {summary.totalRes.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {totalFillRate}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {summary.totalImp.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {summary.totalClk.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {totalCTR}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {totalECPM}
                      </TableCell>
                    </TableRow>

                    {/* 지면별 상세 데이터 */}
                    {isOpen && summary.placements
                      .sort((a, b) => a.placementGroup.localeCompare(b.placementGroup))
                      .map((placement, idx) => {
                        const fillRate = calculateFillRate(placement.req, placement.res);
                        const ctr = calculateCTR(placement.imp, placement.clk);
                        const ecpm = calculateECPM(placement.revenue * exchangeRate, placement.imp);

                        return (
                          <TableRow 
                            key={`${dateKey}-${placement.placementGroup}-${idx}`} 
                            sx={{ 
                              backgroundColor: '#fafafa',
                              height: 32
                            }}
                          >
                            <TableCell sx={{ px: '4px' }}></TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', pl: 4, px: '6px' }}>{placement.placementGroup}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {formatCurrency(placement.revenue * exchangeRate)}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {placement.req.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {placement.res.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {fillRate}%
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {placement.imp.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {placement.clk.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {ctr}%
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {ecpm}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={dateSummaries.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </Box>
    );
  }

